import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Instagram, Plus, Trash2, Play, RefreshCw, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * InstagramSourcesPanel
 * Manages a list of Instagram accounts to monitor for events, and triggers the
 * Apify-powered scrape → Claude classifier → PENDING scraped_events pipeline.
 */
export default function InstagramSourcesPanel({ onRunComplete }) {
  const [sources, setSources] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newCommuneId, setNewCommuneId] = useState('');
  const [postsPerProfile, setPostsPerProfile] = useState(10);
  const [maxAgeDays, setMaxAgeDays] = useState(30);
  const [lastResult, setLastResult] = useState(null);

  const fetchSources = async () => {
    setLoading(true);
    try {
      const [srcRes, comsRes, regsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/scraping/instagram/sources`),
        axios.get(`${API_URL}/api/admin/communes`),
        axios.get(`${API_URL}/api/admin/regions`),
      ]);
      setSources(srcRes.data || []);
      setCommunes(comsRes.data || []);
      setRegions(regsRes.data || []);
    } catch (e) {
      toast.error('No se pudieron cargar las fuentes IG');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSources(); }, []);

  const addSource = async () => {
    if (!newUrl.trim()) return toast.error('URL requerida');
    try {
      await axios.post(`${API_URL}/api/admin/scraping/instagram/sources`, {
        url: newUrl.trim(),
        label: newLabel.trim() || null,
        is_active: true,
        default_commune_id: newCommuneId || null,
      });
      setNewUrl(''); setNewLabel(''); setNewCommuneId('');
      toast.success('Cuenta agregada');
      fetchSources();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al agregar');
    }
  };

  const setSourceCommune = async (s, commune_id) => {
    try {
      await axios.put(`${API_URL}/api/admin/scraping/instagram/sources/${s.id}`, {
        url: s.url, label: s.label, is_active: s.is_active,
        default_commune_id: commune_id || null,
      });
      fetchSources();
    } catch (e) { toast.error('Error al actualizar comuna'); }
  };

  const toggleActive = async (s) => {
    try {
      await axios.put(`${API_URL}/api/admin/scraping/instagram/sources/${s.id}`, {
        url: s.url, label: s.label, is_active: !s.is_active,
        default_commune_id: s.default_commune_id || null,
      });
      fetchSources();
    } catch (e) { toast.error('Error al actualizar'); }
  };

  const removeSource = async (s) => {
    if (!window.confirm(`¿Eliminar @${s.username}? No se borrarán los eventos ya scrapeados.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/scraping/instagram/sources/${s.id}`);
      fetchSources();
    } catch (e) { toast.error('Error al eliminar'); }
  };

  const runNow = async (sourceId = null) => {
    setRunning(true);
    setLastResult(null);
    try {
      const payload = {
        results_per_profile: postsPerProfile,
        max_age_days: maxAgeDays,
        min_confidence: 0.6,
        only_with_image: true,
      };
      if (sourceId) payload.source_ids = [sourceId];
      // 1. Queue the job — returns immediately with a job_id (bypasses Cloudflare 100s timeout).
      const startRes = await axios.post(`${API_URL}/api/admin/scraping/instagram/run`, payload);
      const jobId = startRes.data?.job_id;
      if (!jobId) {
        toast.error(startRes.data?.status === 'no_sources' ? 'No hay cuentas activas' : 'No se pudo iniciar el job');
        setRunning(false);
        return;
      }
      toast.message('Escaneando en segundo plano…', { description: 'Este proceso puede tardar 2-4 minutos.' });
      // 2. Poll every 3s until completed / failed. Max 10 minutes.
      const started = Date.now();
      while (Date.now() - started < 10 * 60_000) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          const jr = await axios.get(`${API_URL}/api/admin/scraping/scraping/jobs/${jobId}`);
          const j = jr.data;
          if (j.status === 'completed') {
            const d = j.result || {};
            setLastResult(d);
            toast.success(`${d.inserted} eventos nuevos · ${d.classified_as_event} clasificados · ${d.posts_seen} posts vistos`);
            fetchSources();
            onRunComplete && onRunComplete();
            break;
          }
          if (j.status === 'failed') {
            toast.error(`Job falló: ${j.error || 'error desconocido'}`);
            break;
          }
        } catch (e) {
          // transient — keep polling
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al ejecutar scraping');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="rounded-2xl bg-gradient-to-br from-fuchsia-50 to-white border border-fuchsia-100 p-5" data-testid="ig-sources-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-fuchsia-500 to-pink-500 text-white flex items-center justify-center shrink-0">
            <Instagram className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-fuchsia-900">Cuentas de Instagram</h3>
            <p className="text-sm text-gray-600 max-w-lg">Agrega perfiles públicos de IG. Apify extrae los últimos posts, Claude detecta si son eventos y los deja en la lista de pendientes para revisar.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 bg-white rounded-lg border px-2 py-1">
            <label className="text-xs text-gray-500 uppercase tracking-wider">Posts / cuenta</label>
            <input type="number" min="1" max="50" value={postsPerProfile} onChange={(e) => setPostsPerProfile(Math.max(1, parseInt(e.target.value) || 10))}
              className="w-12 outline-none text-sm font-semibold" data-testid="ig-posts-per-profile" />
          </div>
          <div className="flex items-center gap-2 bg-white rounded-lg border px-2 py-1">
            <label className="text-xs text-gray-500 uppercase tracking-wider">Antigüedad máx</label>
            <input type="number" min="1" max="365" value={maxAgeDays} onChange={(e) => setMaxAgeDays(Math.max(1, parseInt(e.target.value) || 30))}
              className="w-14 outline-none text-sm font-semibold" data-testid="ig-max-age-days" />
            <span className="text-xs text-gray-400">d</span>
          </div>
          <Button
            onClick={() => runNow(null)}
            disabled={running || sources.filter((s) => s.is_active).length === 0}
            className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
            data-testid="ig-run-all-btn"
          >
            {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
            {running ? 'Ejecutando…' : `Revisar ${sources.filter((s) => s.is_active).length} activas`}
          </Button>
        </div>
      </div>

      {/* Add form */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2 mb-3">
        <Input
          value={newUrl}
          onChange={(e) => setNewUrl(e.target.value)}
          placeholder="https://www.instagram.com/casinoenjoyantofa/"
          data-testid="ig-new-url"
          onKeyDown={(e) => e.key === 'Enter' && addSource()}
        />
        <Input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          placeholder="Etiqueta (opcional)"
          data-testid="ig-new-label"
          onKeyDown={(e) => e.key === 'Enter' && addSource()}
        />
        <Select value={newCommuneId || undefined} onValueChange={setNewCommuneId}>
          <SelectTrigger data-testid="ig-new-commune"><SelectValue placeholder="Comuna por defecto (opcional)" /></SelectTrigger>
          <SelectContent className="max-h-72">
            {communes.map((c) => {
              const r = regions.find((r) => r.id === c.region_id);
              return <SelectItem key={c.id} value={c.id}>{c.name}{r ? ` · ${r.name}` : ''}</SelectItem>;
            })}
          </SelectContent>
        </Select>
        <Button onClick={addSource} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ig-add-btn">
          <Plus className="w-4 h-4 mr-1" /> Agregar
        </Button>
      </div>

      {/* Last run result */}
      {lastResult && (
        <div className="text-xs bg-white border rounded-lg px-3 py-2 mb-3 flex flex-wrap gap-x-4 gap-y-1">
          <span><b>{lastResult.profiles_scraped}</b> cuentas</span>
          <span><b>{lastResult.posts_seen}</b> posts vistos</span>
          <span className="text-fuchsia-700">✨ <b>{lastResult.classified_as_event}</b> detectados como evento</span>
          <span className="text-emerald-700">✓ <b>{lastResult.inserted}</b> ingresados a pendientes</span>
          {lastResult.skipped_duplicate > 0 && <span className="text-gray-500">↺ {lastResult.skipped_duplicate} duplicados</span>}
          {lastResult.skipped_past > 0 && <span className="text-gray-500">⏮ {lastResult.skipped_past} pasados</span>}
          {lastResult.errors?.length > 0 && <span className="text-red-600">⚠ {lastResult.errors.length} errores</span>}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="py-6 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : sources.length === 0 ? (
        <div className="py-6 text-center text-gray-400 text-sm border border-dashed rounded-lg bg-white">
          Aún no agregas ninguna cuenta. Pega una URL arriba para empezar.
        </div>
      ) : (
        <div className="space-y-2">
          {sources.map((s) => (
            <div key={s.id} data-testid={`ig-source-${s.id}`} className="flex items-center gap-3 bg-white border rounded-lg px-3 py-2 flex-wrap">
              <Instagram className="w-4 h-4 text-fuchsia-500 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm text-gray-900 truncate">{s.label || `@${s.username}`}</span>
                  <a href={s.url} target="_blank" rel="noreferrer" className="text-xs text-gray-400 hover:text-emerald-600 inline-flex items-center gap-0.5">
                    @{s.username} <ExternalLink className="w-3 h-3" />
                  </a>
                  {!s.is_active && <Badge variant="outline" className="text-[10px]">Pausada</Badge>}
                  {s.default_commune_name ? (
                    <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200">📍 {s.default_commune_name}</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] text-orange-600 border-orange-300">Sin comuna por defecto</Badge>
                  )}
                </div>
                <p className="text-[11px] text-gray-500">
                  {s.last_scraped_at
                    ? `Último scan: ${new Date(s.last_scraped_at).toLocaleString('es-CL')} · ${s.last_post_count} posts vistos`
                    : 'Aún sin ejecutar'}
                </p>
                {/* Inline commune editor */}
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-[11px] text-gray-400">Comuna:</span>
                  <Select value={s.default_commune_id || undefined} onValueChange={(v) => setSourceCommune(s, v)}>
                    <SelectTrigger className="h-6 text-xs w-52" data-testid={`ig-commune-${s.id}`}>
                      <SelectValue placeholder="Elige comuna" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {communes.map((c) => {
                        const r = regions.find((r) => r.id === c.region_id);
                        return <SelectItem key={c.id} value={c.id}>{c.name}{r ? ` · ${r.name}` : ''}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1 text-[11px] text-gray-500">
                  <Switch checked={s.is_active} onCheckedChange={() => toggleActive(s)} data-testid={`ig-toggle-${s.id}`} />
                </label>
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={running || !s.is_active} onClick={() => runNow(s.id)} data-testid={`ig-run-${s.id}`}>
                  <RefreshCw className={`w-3 h-3 mr-1 ${running ? 'animate-spin' : ''}`} /> Escanear
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600 hover:bg-red-50" onClick={() => removeSource(s)} data-testid={`ig-delete-${s.id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
